import { useEffect, useState } from "react";
import { TopBar } from "../components/TopBar";
import { TabBar } from "../components/TabBar";
import { api } from "../api/client";
import { useExerciseLibrary } from "../hooks/useExerciseLibrary";
import type { Exercise, Profile } from "../api/types";

export const Admin = () => {
  const [profile, setProfile] = useState<Profile | null>(null);
  const { data: library } = useExerciseLibrary();
  const [editing, setEditing] = useState<Exercise | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    api<Profile>("/me").then(setProfile).catch(() => {});
  }, []);

  if (profile && !profile.isAdmin) {
    return (
      <div className="page">
        <TopBar title="Admin" back />
        <div className="error">You are not an admin.</div>
      </div>
    );
  }

  const save = async () => {
    if (!editing) return;
    setBusy(true);
    setMsg(null);
    try {
      if (library.find((e) => e.id === editing.id)) {
        await api(`/admin/exercises/${editing.id}`, { method: "PUT", body: editing });
      } else {
        await api("/admin/exercises", { method: "POST", body: editing });
      }
      setMsg("Saved");
    } finally {
      setBusy(false);
    }
  };

  const uploadImage = async (file: File) => {
    if (!editing) return;
    setBusy(true);
    setMsg(null);
    try {
      const { url, key } = await api<{ url: string; key: string }>(
        `/admin/exercises/${editing.id}/image-url`,
        { method: "POST" },
      );
      const put = await fetch(url, {
        method: "PUT",
        headers: { "Content-Type": "image/jpeg" },
        body: file,
      });
      if (!put.ok) throw new Error(`upload failed (${put.status})`);
      setEditing({ ...editing, imageKey: key });
      setMsg("Uploaded — remember to Save to persist the key");
    } catch (e) {
      setMsg(e instanceof Error ? e.message : "upload failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="page">
        <TopBar title="Admin" back />

        {!editing ? (
          <>
            <button
              className="btn"
              onClick={() =>
                setEditing({
                  id: "",
                  name: "",
                  muscleGroup: "chest",
                  equipment: "barbell",
                  difficulty: "beginner",
                  primaryMuscles: [],
                  secondaryMuscles: [],
                  description: "",
                  form: [],
                  mistakes: [],
                })
              }
            >
              + New exercise
            </button>
            <div className="muted" style={{ marginTop: 8 }}>
              Edit existing:
            </div>
            <div style={{ marginTop: 8 }}>
              {library.map((ex) => (
                <button
                  key={ex.id}
                  className="list-card"
                  onClick={() => setEditing({ ...ex })}
                >
                  <div className="thumb">{ex.name.charAt(0)}</div>
                  <div>
                    <div style={{ fontWeight: 700 }}>{ex.name}</div>
                    <div className="muted">
                      {ex.muscleGroup} · {ex.equipment}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </>
        ) : (
          <div className="card">
            {msg && <div className="banner">{msg}</div>}
            <label className="field">
              ID (slug)
              <input
                value={editing.id}
                onChange={(e) => setEditing({ ...editing, id: e.target.value })}
              />
            </label>
            <label className="field" style={{ marginTop: 8 }}>
              Name
              <input
                value={editing.name}
                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
              />
            </label>
            <div className="row" style={{ marginTop: 8, gap: 8 }}>
              <label className="field" style={{ flex: 1 }}>
                Muscle group
                <input
                  value={editing.muscleGroup}
                  onChange={(e) => setEditing({ ...editing, muscleGroup: e.target.value })}
                />
              </label>
              <label className="field" style={{ flex: 1 }}>
                Equipment
                <input
                  value={editing.equipment}
                  onChange={(e) => setEditing({ ...editing, equipment: e.target.value })}
                />
              </label>
              <label className="field" style={{ flex: 1 }}>
                Difficulty
                <select
                  value={editing.difficulty}
                  onChange={(e) =>
                    setEditing({
                      ...editing,
                      difficulty: e.target.value as Exercise["difficulty"],
                    })
                  }
                >
                  <option value="beginner">beginner</option>
                  <option value="intermediate">intermediate</option>
                  <option value="advanced">advanced</option>
                </select>
              </label>
            </div>
            <label className="field" style={{ marginTop: 8 }}>
              Description
              <textarea
                rows={3}
                value={editing.description ?? ""}
                onChange={(e) => setEditing({ ...editing, description: e.target.value })}
              />
            </label>
            <label className="field" style={{ marginTop: 8 }}>
              Image
              <input
                type="file"
                accept="image/jpeg"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) void uploadImage(f);
                }}
              />
              {editing.imageKey && (
                <span className="muted">Key: {editing.imageKey}</span>
              )}
            </label>
            <div className="row" style={{ marginTop: 12, gap: 8 }}>
              <button className="btn secondary" onClick={() => setEditing(null)}>Cancel</button>
              <button className="btn" disabled={busy} onClick={() => void save()}>
                {busy ? "Working…" : "Save"}
              </button>
            </div>
          </div>
        )}
      </div>
      <TabBar />
    </>
  );
};
